import { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';

const WorkflowDescriptionModal = ({
  open,
  onClose,
  isCanvasLocked,
  handleSaveProjectAttributes,
  workflow,
  setWorkflow,
}) => {
  const [workflowDescriptionInTextbox, setWorkflowDescriptionInTextbox] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setWorkflowDescriptionInTextbox(workflow?.description || '');
    }
  }, [open, workflow?.description]);

  const handleDescriptionChange = event => {
    setWorkflowDescriptionInTextbox(event.target.value);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await handleSaveProjectAttributes({ description: workflowDescriptionInTextbox });
      setWorkflow(prev => ({ ...prev, description: workflowDescriptionInTextbox }));
      onClose();
    } catch (error) {
      console.error('Error saving workflow description:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(4px)',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 0.5,
          pt: 2.5,
          px: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '1.05rem',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: 'text.primary',
          }}
        >
          Workflow Description
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={isSaving}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1, pb: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 2,
            fontSize: '0.825rem',
            lineHeight: 1.5,
          }}
        >
          Describe what this workflow does, its purpose, and how it should be used.
        </Typography>

        <TextField
          multiline
          minRows={6}
          maxRows={16}
          fullWidth
          variant="outlined"
          value={workflowDescriptionInTextbox}
          onChange={handleDescriptionChange}
          placeholder="Enter workflow description..."
          disabled={isCanvasLocked || isSaving}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              fontSize: '0.875rem',
              lineHeight: 1.6,
              transition: 'border-color 0.2s, box-shadow 0.2s',
              '& fieldset': {
                borderColor: 'divider',
              },
              '&:hover fieldset': {
                borderColor: 'text.disabled',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '1.5px',
              },
            },
          }}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1.5,
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          disabled={isSaving}
          size="small"
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
            fontWeight: 500,
            fontSize: '0.825rem',
            px: 2,
            borderRadius: '7px',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={isCanvasLocked || isSaving}
          size="small"
          disableElevation
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.825rem',
            px: 2.5,
            borderRadius: '7px',
            minWidth: 80,
          }}
          startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowDescriptionModal;
